"use client";

// @storybook-internal focus rows catalogued through ChatComposer.
import {
  Bell,
  CalendarDays,
  ChartNoAxesCombined,
  Globe,
  List,
  PieChart,
  Sparkles,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  buildPremadeQuestionTappedProperties,
  buildPremadeQuestionsShownProperties,
  E_ShellSuggestionPlacement,
  filterShellSuggestions,
  SHELL_COMPOSER_SUGGESTIONS,
  type T_ShellChatSurface,
  type T_ShellSuggestionIcon,
} from "@lattice/shared-frontend/shell";

import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";

export type T_ComposerFocusSuggestionsProps = {
  chatSurface: T_ShellChatSurface;
  /** Live composer text; narrows the list token-wise as the user types. */
  filterQuery?: string;
  onPress: (promptText: string) => void;
};

function getSuggestionIcon(icon: T_ShellSuggestionIcon): ReactNode {
  switch (icon) {
    case "bell":
      return <Bell aria-hidden size={22} />;
    case "calendar":
      return <CalendarDays aria-hidden size={22} />;
    case "globe":
      return <Globe aria-hidden size={22} />;
    case "list":
      return <List aria-hidden size={22} />;
    case "market":
      return <ChartNoAxesCombined aria-hidden size={22} />;
    case "portfolio":
      return <PieChart aria-hidden size={22} />;
    case "sparkle":
      return <Sparkles aria-hidden size={22} />;
  }
}

/** Native-parity focus suggestions with shared copy, ordering, and analytics. */
export function ComposerFocusSuggestions({
  chatSurface,
  filterQuery = "",
  onPress,
}: T_ComposerFocusSuggestionsProps) {
  const impressionTrackedRef = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- Mounting this visible focus state is an analytics impression; the ref prevents StrictMode replay duplication.
  useEffect(() => {
    if (impressionTrackedRef.current) return;
    impressionTrackedRef.current = true;
    const shown = buildPremadeQuestionsShownProperties(
      {
        chatSurface,
        placement: E_ShellSuggestionPlacement.COMPOSER_FOCUS,
      },
      SHELL_COMPOSER_SUGGESTIONS,
    );
    trackEvent(MixpanelEvents.SHELL_PREMADE_QUESTIONS_SHOWN, {
      ...shown,
      question_ids: shown.question_ids.join(","),
    });
  }, [chatSurface]);

  const visibleSuggestions = filterShellSuggestions(
    SHELL_COMPOSER_SUGGESTIONS,
    filterQuery,
  );

  // Edge fades signal hidden rows: faded at an edge iff more content lies
  // beyond it, so the reachable edge renders crisp.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ atBottom: true, atTop: true });
  const updateEdges = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const atTop = el.scrollTop <= 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setEdges((current) =>
      current.atTop === atTop && current.atBottom === atBottom
        ? current
        : { atBottom, atTop },
    );
  }, []);
  // eslint-disable-next-line no-restricted-syntax -- Scroll geometry lives on the DOM node; re-measure when the filtered row set changes.
  useEffect(() => {
    updateEdges();
  }, [updateEdges, visibleSuggestions.length]);

  if (visibleSuggestions.length === 0) return null;

  return (
    <div
      aria-label="Suggested Prism questions"
      className={[
        "chat-v8-composer-suggestions",
        edges.atTop ? "" : "chat-v8-composer-suggestions--fade-top",
        edges.atBottom ? "" : "chat-v8-composer-suggestions--fade-bottom",
      ]
        .filter(Boolean)
        .join(" ")}
      onScroll={updateEdges}
      ref={listRef}
      role="group"
    >
      {visibleSuggestions.map((suggestion) => (
        <button
          className="chat-v8-composer-suggestion"
          key={suggestion.id}
          onClick={() => {
            trackEvent(
              MixpanelEvents.SHELL_PREMADE_QUESTION_TAPPED,
              buildPremadeQuestionTappedProperties(
                {
                  chatSurface,
                  placement: E_ShellSuggestionPlacement.COMPOSER_FOCUS,
                },
                suggestion,
                // Canonical position, not the filtered one, so the
                // question_index funnel stays comparable across queries.
                SHELL_COMPOSER_SUGGESTIONS.indexOf(suggestion),
              ),
            );
            onPress(suggestion.text);
          }}
          onPointerDown={(event) => event.preventDefault()}
          type="button"
        >
          <span className="chat-v8-composer-suggestion__icon">
            {getSuggestionIcon(suggestion.icon)}
          </span>
          <span>{suggestion.text}</span>
        </button>
      ))}
    </div>
  );
}
